/**
 * Layout geometry for the map: one uniform, aspect-preserving transform from the
 * layout's raw frame (OpenF1 location units, 0.1 m) into a fixed SVG viewBox.
 * Bounds are fixed per layout, so no packet can zoom or stretch the circuit.
 */

export const MAP_VIEWBOX = { width: 1000, height: 640, padding: 56 };

function rotatePoint(x, y, angleRad) {
    const c = Math.cos(angleRad);
    const s = Math.sin(angleRad);
    return [x * c - y * s, x * s + y * c];
}

/**
 * Build a projector for a layout. Rotation (degrees, from the layout's
 * reference data) orients the circuit the familiar way; y is flipped because
 * SVG y grows downwards while the provider frame grows upwards.
 */
export function buildProjector(layout, { viewBox = MAP_VIEWBOX, rotationDeg = null } = {}) {
    if (!layout || !Array.isArray(layout.path) || layout.path.length < 3) return null;
    const angle = ((rotationDeg ?? layout.rotation_deg ?? 0) * Math.PI) / 180;
    const rotated = layout.path.map(([x, y]) => rotatePoint(x, -y, angle));
    const pitRotated = layout.pit_lane?.path ? layout.pit_lane.path.map(([x, y]) => rotatePoint(x, -y, angle)) : [];
    const all = rotated.concat(pitRotated);
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    all.forEach(([x, y]) => {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
    });
    const spanX = Math.max(1, maxX - minX);
    const spanY = Math.max(1, maxY - minY);
    const scale = Math.min((viewBox.width - 2 * viewBox.padding) / spanX, (viewBox.height - 2 * viewBox.padding) / spanY);
    const offsetX = (viewBox.width - spanX * scale) / 2;
    const offsetY = (viewBox.height - spanY * scale) / 2;
    const toScreen = (x, y) => {
        const [rx, ry] = rotatePoint(x, -y, angle);
        return { x: offsetX + (rx - minX) * scale, y: offsetY + (ry - minY) * scale };
    };
    const points = layout.path.map(([x, y]) => toScreen(x, y));
    const cumulative = [0];
    for (let i = 1; i < points.length; i += 1) {
        cumulative.push(cumulative[i - 1] + Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y));
    }
    const length = cumulative[cumulative.length - 1] || 1;
    const pitPoints = layout.pit_lane?.path ? layout.pit_lane.path.map(([x, y]) => toScreen(x, y)) : [];
    const pitCumulative = [0];
    for (let i = 1; i < pitPoints.length; i += 1) {
        pitCumulative.push(pitCumulative[i - 1] + Math.hypot(pitPoints[i].x - pitPoints[i - 1].x, pitPoints[i].y - pitPoints[i - 1].y));
    }
    const pitLength = pitCumulative[pitCumulative.length - 1] || 1;

    function segmentFor(cum, distance) {
        let lo = 0;
        let hi = cum.length - 2;
        while (lo < hi) {
            const mid = (lo + hi + 1) >> 1;
            if (cum[mid] <= distance) lo = mid;
            else hi = mid - 1;
        }
        return Math.max(0, Math.min(lo, cum.length - 2));
    }

    function pointOn(pts, cum, total, fraction) {
        if (pts.length < 2) return { x: 0, y: 0, heading: 0 };
        const distance = Math.max(0, Math.min(0.999999, fraction)) * total;
        const index = segmentFor(cum, distance);
        const a = pts[index];
        const b = pts[index + 1];
        const segLen = cum[index + 1] - cum[index];
        const t = segLen > 0 ? (distance - cum[index]) / segLen : 0;
        return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, heading: (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI };
    }

    const pathD = points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
    const pitD = pitPoints.length > 1 ? pitPoints.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ") : null;

    return {
        viewBox,
        scale,
        toScreen,
        pathD,
        pitD,
        points,
        pitPoints,
        lengthUnits: layout.length_m ? layout.length_m * 10 : null,
        pointAt: (progress) => pointOn(points, cumulative, length, ((progress % 1) + 1) % 1),
        pitPointAt: (fraction) => pointOn(pitPoints, pitCumulative, pitLength, fraction),
        hasPit: pitPoints.length > 1,
        sectorLines: (layout.sectors || []).map((sector) => ({ sector: sector.sector, ...pointOn(points, cumulative, length, sector.end_progress % 1) })),
        corners: (layout.corners || []).map((corner) => ({ ...corner, screen: toScreen(corner.label_x ?? corner.x, corner.label_y ?? corner.y), onPath: pointOn(points, cumulative, length, corner.progress) })),
        startFinish: pointOn(points, cumulative, length, 0),
        metresPerUnit: scale / 10,
    };
}

/**
 * The same projector magnified k times around a point of its own screen frame (cx, cy), re-centred in the
 * viewBox. Every coordinate goes through one extra uniform transform, so glyphs, labels and stroke widths keep
 * their screen size while geometry and samples stay mutually aligned. Used by the developer inspector's zoom.
 */
export function zoomProjector(projector, { cx, cy, k }) {
    if (!projector || !(k > 1)) return projector;
    const { width, height } = projector.viewBox;
    const map = (point) => ({ ...point, x: width / 2 + (point.x - cx) * k, y: height / 2 + (point.y - cy) * k });
    const toD = (pts) => pts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
    const points = projector.points.map(map);
    return {
        ...projector,
        zoom: { cx, cy, k },
        scale: projector.scale * k,
        metresPerUnit: projector.metresPerUnit * k,
        toScreen: (x, y) => map(projector.toScreen(x, y)),
        points,
        pathD: toD(points),
        pitD: projector.pitD && projector.pitPoints ? toD(projector.pitPoints.map(map)) : null,
        pitPoints: projector.pitPoints ? projector.pitPoints.map(map) : [],
        pointAt: (progress) => map(projector.pointAt(progress)),
        pitPointAt: (fraction) => map(projector.pitPointAt(fraction)),
        sectorLines: projector.sectorLines.map(map),
        corners: projector.corners.map((corner) => ({ ...corner, screen: map(corner.screen), onPath: map(corner.onPath) })),
        startFinish: map(projector.startFinish),
    };
}

export function circularDelta(progress, reference) {
    let delta = progress - reference;
    if (delta > 0.5) delta -= 1;
    else if (delta < -0.5) delta += 1;
    return delta;
}
